import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import usePaymentTerms from '~hooks/usePaymentTerms/usePaymentTerms';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import PaymentTermForm, { PaymentTermFormHandler } from './PaymentTermForm';
import { getPaymentTermAsyncErrors, PaymentTermDtoAsyncError } from './paymentTermSchema';

type PaymentTermDialogState = {
  isOpen: boolean;
  isSubmitting: boolean;
  paymentTerm?: PaymentTerm;
};

export const PaymentTermsManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { create, fetchPage, update } = usePaymentTerms();

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_API_PAGE_SIZE,
    page: 1,
  });
  const [isLoading, setIsLoading] = useState(false);

  const paymentTermFormRef = useRef<PaymentTermFormHandler>(null);
  const [formDialogState, setFormDialogState] = useState<PaymentTermDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [formDialogAsyncErrors, setFormDialogAsyncErrors] = useState<
    PaymentTermDtoAsyncError[]
  >([]);

  const [termIdToDeactivate, setTermIdToDeactivate] = useState<string | undefined>();

  const loadPage = useCallback(
    async (link?: PaginationLink) => {
      if (!companyId) {
        return;
      }

      setIsLoading(true);
      const cursor = link ? pagination[link] : undefined;
      const result = await fetchPage({ companyId, cursor, link });

      if (result) {
        setPaymentTerms(result.data);
        setPagination((previous) => ({
          ...result.pagination,
          limit: previous.limit,
          page:
            link === 'after'
              ? previous.page + 1
              : link === 'before'
                ? Math.max(previous.page - 1, 1)
                : 1,
        }));
      }

      setIsLoading(false);
    },
    [companyId, fetchPage, pagination],
  );

  useEffect(() => {
    loadPage();
  }, [companyId]);

  useEffect(() => {
    if (!formDialogState.isOpen) {
      setFormDialogAsyncErrors([]);
    }
  }, [formDialogState.isOpen]);

  const handleCloseFormDialog = useCallback(() => {
    if (formDialogState.isSubmitting) {
      return;
    }

    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, [formDialogState.isSubmitting]);

  const handleSubmitFormDialog = useCallback(async () => {
    const formData = await paymentTermFormRef.current?.onSubmit();

    if (!formData || !companyId) {
      return;
    }

    const paymentTermId = formDialogState.paymentTerm?.id;
    const body = { name: formData.name, due_days: Number(formData.dueDays) };

    try {
      setFormDialogAsyncErrors([]);
      setFormDialogState((state) => ({ ...state, isSubmitting: true }));

      if (paymentTermId) {
        const updated = await update(companyId, paymentTermId, body);
        setPaymentTerms((prev) =>
          prev.map((term) => (term.id === paymentTermId ? updated : term)),
        );
      } else {
        const created = await create(companyId, body);
        setPaymentTerms((prev) => [created, ...prev]);
      }

      toasterStore.push(
        alert(t('administration.payment_terms.saved'), AlertTypes.success),
      );
      setFormDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setFormDialogState((state) => ({ ...state, isSubmitting: false }));

      const asyncErrors = getPaymentTermAsyncErrors(error);

      // A non-422 failure already toasted inside create/update (connection.handleRequestError);
      // no further handling needed here or the user sees the same toast twice.
      if (asyncErrors.length) {
        setFormDialogAsyncErrors(asyncErrors);
      }
    }
  }, [companyId, create, formDialogState.paymentTerm?.id, toasterStore, update]);

  const setTermActive = useCallback(
    async (termId: string, active: boolean) => {
      if (!companyId) {
        return false;
      }

      try {
        const updated = await update(companyId, termId, { active });
        setPaymentTerms((prev) =>
          prev.map((term) => (term.id === termId ? updated : term)),
        );

        return true;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          const serverMessage = error.response.data?.error?.errors?.[0]?.message;
          toasterStore.push(
            alert(
              serverMessage ?? t('error_messages.payment_terms.failed_to_save'),
              AlertTypes.error,
            ),
          );
        }

        return false;
      }
    },
    [companyId, toasterStore, update],
  );

  const columns = useMemo<Array<DataTableColumn<PaymentTerm>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        renderCell: (paymentTerm) => (
          <span data-test-id={`payment-term-name-${paymentTerm.id}`}>
            {paymentTerm.name}
          </span>
        ),
      },
      {
        field: 'dueDays',
        headerName: t('administration.payment_terms.due_days'),
        valueGetter: (paymentTerm) => paymentTerm.dueDays,
      },
      {
        field: 'active',
        headerName: t('form_fields.status'),
        renderCell: (paymentTerm) => (
          <Chip
            label={paymentTerm.active ? t('common.active') : t('common.inactive')}
            color={paymentTerm.active ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
      // TODO: use the shared created-on column https://linear.app/treadapp/issue/TRE-18155
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (paymentTerm) =>
          paymentTerm.createdAt ? dateFormat(paymentTerm.createdAt) : null,
      },
      actionsColumn((paymentTerm) => {
        const options = [
          {
            title: t('actions.edit'),
            icon: <Edit />,
            callBack: () => {
              setFormDialogState({
                isOpen: true,
                isSubmitting: false,
                paymentTerm,
              });
            },
          },
          paymentTerm.active
            ? {
                title: t('actions.deactivate'),
                icon: <ToggleOffOutlined />,
                callBack: () => setTermIdToDeactivate(paymentTerm.id),
              }
            : {
                title: t('actions.activate'),
                icon: <ToggleOnOutlined />,
                callBack: () => void setTermActive(paymentTerm.id, true),
              },
        ];

        return (
          <SimpleMenu options={options}>
            <MoreHoriz />
          </SimpleMenu>
        );
      }),
    ],
    [paymentTerms, setTermActive],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.payment_terms.title')}
        description={t('administration.payment_terms.description')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          onClick={() => setFormDialogState({ isOpen: true, isSubmitting: false })}
          startIcon={<Add />}
        >
          {t('administration.payment_terms.create_term')}
        </SmallButton>
      </Box>
      <Box data-test-id="payment-terms-data-grid">
        <DataTable
          id="payment-terms-data-grid"
          columns={columns}
          rows={paymentTerms}
          loading={isLoading}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isLoading}
              onNavigate={loadPage}
              pagination={pagination}
            />
          }
        />
      </Box>

      <Dialog
        isOpen={formDialogState.isOpen}
        onClose={handleCloseFormDialog}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
        sx={{ [`.${paperClasses.root}`]: { maxWidth: '700px' } }}
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.paymentTerm
            ? t('administration.payment_terms.edit_term')
            : t('administration.payment_terms.create_term')}
        </DialogHeader>

        <DialogContent>
          <PaymentTermForm
            ref={paymentTermFormRef}
            asyncErrors={formDialogAsyncErrors}
            paymentTerm={formDialogState.paymentTerm}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(formDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={formDialogState.isSubmitting}
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitFormDialog}
          >
            {formDialogState.paymentTerm ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <DeactivateConfirmationDialog
        id={termIdToDeactivate}
        message={t('administration.payment_terms.deactivate_description')}
        onClose={() => setTermIdToDeactivate(undefined)}
        onConfirm={async () => {
          if (termIdToDeactivate) {
            const succeeded = await setTermActive(termIdToDeactivate, false);

            if (succeeded) {
              setTermIdToDeactivate(undefined);
            }
          }
        }}
        title={t('actions.deactivate')}
      />
    </>
  );
});
